// Libraries
import currency from 'currency.js';

// Supermove
import {gql} from '@supermove/graphql';
import {BillRuleType, BillRuleTypeModel} from '@supermove/models';
import {Currency, Float, withFragment} from '@supermove/utils';

// App
import {BillRuleKindType} from '@shared/modules/Billing/enums/BillRuleKind';

const getIsCurrency = ({kind}: {kind: BillRuleKindType | undefined}) =>
  BillRuleType.getValueType(kind) === 'CURRENCY';
const getIsString = ({kind}: {kind: BillRuleKindType | undefined}) =>
  BillRuleType.getValueType(kind) === 'STRING';

type ConvertToCentsArgs = currency.Any | undefined | null;

const getFormValueFromInput = ({
  valueFormulaId,
  kind,
  value,
}: {
  valueFormulaId: string | null;
  kind: BillRuleKindType | undefined;
  value: string | ConvertToCentsArgs;
}): string => {
  if (valueFormulaId) {
    return '';
  }
  if (getIsString({kind})) {
    const stringValue = value as string;
    return stringValue;
  }
  if (getIsCurrency({kind})) {
    return Currency.toForm(Currency.convertToCents(value));
  }
  return Float.toForm(value as string);
};

const getFormValue = ({
  valueFormulaId,
  kind,
  value,
  stringValue,
}: {
  valueFormulaId: string | null;
  kind: BillRuleKindType | undefined;
  value: number | '' | null | undefined;
  stringValue: string | undefined | null;
}): string => {
  if (valueFormulaId) {
    return '';
  }
  if (getIsString({kind})) {
    return stringValue || '';
  }
  if (getIsCurrency({kind})) {
    return Currency.toForm(value);
  }
  return Float.toForm(value);
};

const getMutationValue = ({
  valueFormulaId,
  kind,
  value,
}: {
  valueFormulaId: string | null;
  kind: BillRuleKindType | undefined;
  value: string | ConvertToCentsArgs;
}) => {
  if (valueFormulaId || getIsString({kind})) {
    return null;
  }
  if (getIsCurrency({kind})) {
    return Currency.toMutation(value);
  }
  return Float.toMutation(value as string);
};

export interface BillRuleTypeFormType
  extends Omit<BillRuleTypeModel, 'id' | 'kind' | 'organization' | 'billingLibrary'> {
  billRuleTypeId: string | null;
  kind: BillRuleKindType | undefined;
}

const edit = withFragment(
  (billRuleType: BillRuleTypeModel, {userId}: {userId: string}): BillRuleTypeFormType => ({
    billingLibraryId: billRuleType.billingLibraryId,
    billRuleTypeId: billRuleType.id,
    organizationId: billRuleType.organizationId,
    descriptionFormulaId: billRuleType.descriptionFormulaId,
    nameFormulaId: billRuleType.nameFormulaId,
    valueFormulaId: billRuleType.valueFormulaId,
    kind: billRuleType.kind,
    name: billRuleType.name,
    description: billRuleType.description,
    value: billRuleType.value,
    stringValue: billRuleType.stringValue,
    isVisibleToCustomer: billRuleType.isVisibleToCustomer,
    identifier: billRuleType.identifier,
    createdById: billRuleType.createdById,
    updatedById: userId,
    billItemTypeId: billRuleType.billItemTypeId,
  }),
  gql`
    fragment BillRuleTypeForm_edit on BillRuleType {
      id
      billingLibraryId
      organizationId
      descriptionFormulaId
      nameFormulaId
      valueFormulaId
      kind
      name
      description
      value
      stringValue
      isVisibleToCustomer
      identifier
      createdById
      updatedById
      billItemTypeId
    }
  `,
);

const _new = ({
  billingLibraryId,
  organizationId,
  userId,
  kind,
}: {
  billingLibraryId: string;
  organizationId: string;
  kind?: BillRuleKindType;
  userId: string;
}): BillRuleTypeFormType => ({
  kind,
  name: '',
  description: '',
  value: null,
  stringValue: '',
  isVisibleToCustomer: false,
  billRuleTypeId: null,
  billingLibraryId,
  organizationId,
  descriptionFormulaId: null,
  nameFormulaId: null,
  valueFormulaId: null,
  identifier: null,
  createdById: userId,
  updatedById: userId,
  billItemTypeId: null,
});

export interface BillRuleTypeFormToFormType {
  kind: BillRuleKindType | undefined;
  name: string;
  description: string;
  value: string;
  isVisibleToCustomer: boolean;
  billRuleTypeId: string | null;
  billingLibraryId: string;
  organizationId: string;
  descriptionFormulaId: string | null;
  nameFormulaId: string | null;
  valueFormulaId: string | null;
  identifier: string | null;
  createdById: string;
  updatedById: string;
  billItemTypeId: string | null;
}

const toForm = ({
  kind,
  name,
  description,
  value,
  stringValue,
  isVisibleToCustomer,
  billRuleTypeId,
  billingLibraryId,
  organizationId,
  descriptionFormulaId,
  nameFormulaId,
  valueFormulaId,
  identifier,
  createdById,
  updatedById,
  billItemTypeId,
}: BillRuleTypeFormType): BillRuleTypeFormToFormType => {
  return {
    kind,
    name,
    description,
    value: getFormValue({valueFormulaId, kind, value, stringValue}),
    isVisibleToCustomer,
    billRuleTypeId,
    billingLibraryId,
    organizationId,
    descriptionFormulaId,
    nameFormulaId,
    valueFormulaId,
    identifier,
    createdById,
    updatedById,
    billItemTypeId,
  };
};

const toMutation = ({
  kind,
  name,
  description,
  value,
  isVisibleToCustomer,
  billingLibraryId,
  billRuleTypeId,
  organizationId,
  descriptionFormulaId,
  nameFormulaId,
  valueFormulaId,
  identifier,
  createdById,
  updatedById,
  billItemTypeId,
}: BillRuleTypeFormToFormType): BillRuleTypeFormType => {
  return {
    kind,
    name,
    description,
    value: getMutationValue({valueFormulaId, kind, value}),
    stringValue: getIsString({kind}) ? value : '',
    isVisibleToCustomer,
    billingLibraryId,
    billRuleTypeId,
    organizationId,
    descriptionFormulaId,
    nameFormulaId,
    valueFormulaId,
    identifier,
    createdById,
    updatedById,
    billItemTypeId,
  };
};

const BillRuleTypeForm = {
  edit,
  new: _new,
  toForm,
  toMutation,

  getDisplayKind: BillRuleType.getDisplayKind,
  getDisplayValue: BillRuleType.getDisplayValue,
  getIsString,
  getIsCurrency,
  getFormValueFromInput,
  getMutationValue,
};

export default BillRuleTypeForm;
